// Der REST-Client.
//
// Alle Zuege (kaufen, bauen, abholen, zuenden) laufen hierueber — der Server
// entscheidet, der Client fragt an. Die Antwort ist immer der **vollstaendige**
// neue Spielstand: so kann kein Zwischenzustand entstehen, in dem der Client
// eine Stufe gekauft glaubt, die der Server abgelehnt hat.
//
// `try/catch` ist hier erlaubt (Gate FG-13): das Netz darf ausfallen, und dann
// faellt das Spiel in den Solo-Modus zurueck, statt stehen zu bleiben.

import type { Balancing } from '../data/types';
import type { SaveState } from '../state/store';

export interface ApiError {
  code: string;
  message: string;
}

export interface PlayerInfo {
  id: number;
  name: string;
  guest: boolean;
}

export interface SavePayload {
  revision: number;
  save: SaveState;
  stats: Record<string, unknown>;
  xpNeeded: number;
  quests: Array<{ id: string; progress: number; done: boolean; claimed: boolean }>;
  nodes: Array<{ id: string; level: number; cost: number | null; unlocked: boolean; maxed: boolean }>;
  nextBuild: string | null;
  claimable: number;
}

/** Ein Platz der Bestenliste. Der Name kommt von einem anderen Menschen. */
export interface RankingRow {
  rank: number;
  name: string;
  score: number;
  built: number;
  level: number;
  me: boolean;
}

export interface RankingPayload {
  board: RankingBoard;
  span: RankingSpan;
  total: number;
  rows: RankingRow[];
}

/** Die Züge der Kolonie (Akt III) — je einer ein Endpunkt unter `savegame/colony/`. */
export type ColonyOp = 'land' | 'place' | 'upgrade' | 'recipe' | 'switch' | 'relocate' | 'project';

/**
 * Was ein Kolonie-Zug mitschickt. **Felder statt einer zusammengesetzten ID**:
 * der Server prüft ihre Form mit einem Serializer, statt „kargstein:3:4" an
 * drei Stellen zu zerlegen.
 */
export interface ColonyMove {
  planet: string;
  kind?: string;
  x?: number;
  y?: number;
  recipe?: string;
  on?: boolean;
  tx?: number;
  ty?: number;
  times?: number;
}

export type RankingBoard = 'yield' | 'progress';
export type RankingSpan = 'week' | 'all';

export class ApiFehler extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'ApiFehler';
  }
}

const TOKEN_KEY = 'far-mining-token';

export class Api {
  private token: string | null = null;

  constructor(readonly baseUrl: string) {
    this.token = readToken();
  }

  get authenticated(): boolean {
    return this.token !== null;
  }

  /** Gastkonto anlegen oder das gespeicherte Token weiterverwenden. */
  async ensureSession(name?: string): Promise<PlayerInfo | null> {
    if (this.token) {
      // `me()` statt eines erfundenen Spielers: sonst wuesste der Client bei
      // wiederkehrendem Token weder seinen Namen noch, ob er Gast ist — und
      // koennte den Hinweis „Konto sichern" nicht ehrlich zeigen.
      const existing = await this.tryMe();
      if (existing) return existing;
      this.token = null;                       // Token ist abgelaufen oder ungueltig
    }
    const body = await this.request<{ token: string; player: PlayerInfo }>(
      'POST', '/api/v1/auth/guest/', name ? { name } : {},
    );
    this.adoptToken(body.token);
    return body.player;
  }

  async me(): Promise<PlayerInfo> {
    const body = await this.request<{ player: PlayerInfo }>('GET', '/api/v1/auth/me/');
    return body.player;
  }

  /** Aus dem laufenden Gastkonto ein richtiges machen — ohne den Stand zu verlieren. */
  async register(username: string, password: string, name?: string): Promise<PlayerInfo> {
    const body = await this.request<{ token: string; player: PlayerInfo }>(
      'POST', '/api/v1/auth/register/', { username, password, ...(name ? { name } : {}) },
    );
    this.adoptToken(body.token);
    return body.player;
  }

  async login(username: string, password: string): Promise<PlayerInfo> {
    const body = await this.request<{ token: string; player: PlayerInfo }>(
      'POST', '/api/v1/auth/login/', { username, password },
    );
    this.adoptToken(body.token);
    return body.player;
  }

  /** Abmelden ist rein oertlich: der Token bleibt serverseitig gueltig. */
  forgetToken(): void {
    this.token = null;
    clearToken();
  }

  private adoptToken(token: string): void {
    this.token = token;
    writeToken(token);
  }

  async balancing(): Promise<Balancing> {
    return this.request<Balancing>('GET', '/api/v1/balancing/');
  }

  async savegame(): Promise<SavePayload> {
    return this.request<SavePayload>('GET', '/api/v1/savegame/');
  }

  /**
   * Die Bestenliste. Ein `GET` wie jeder andere — **keine** vierte
   * Client-Nachricht (Gate FG-32): der WebSocket kennt genau drei, und eine
   * Rangliste ist nichts, was der Takt der Simulation braucht.
   */
  async ranking(board: RankingBoard, span: RankingSpan): Promise<RankingPayload | null> {
    try {
      return await this.request<RankingPayload>(
        'GET', `/api/v1/ranking/?board=${board}&span=${span}`,
      );
    } catch {
      // Eine nicht erreichbare Bestenliste ist kein Fehlerfall, sondern ein
      // Zustand, den der Reiter anzeigt. Das `catch` bleibt damit **hier**,
      // im Netzmodul — in der Verdrahtung waere es ein `catch` um
      // Spiellogik (Gate FG-13, `netzwerk.md`).
      return null;
    }
  }

  async buyNode(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/node/', { id });
  }

  async build(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/build/', { id });
  }

  async claimQuest(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/quest/', { id });
  }

  async claimSide(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/side/', { id });
  }

  async buyBooster(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/booster/', { id });
  }

  async spendPoint(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/perk/', { id });
  }

  /**
   * Umwandeln — der einzige Zug mit einer Menge.
   *
   * `times` ist eine Absicht: der Server klemmt sie auf das Machbare
   * (`PROGRESS-001`). Wie viele Durchlaeufe es wurden, steht nicht in der
   * Antwort — sie ist wie bei jedem Zug der vollstaendige Spielstand.
   */
  async convert(id: string, times = 1): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/convert/', { id, times });
  }

  async toggleOre(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/ore/', { id });
  }

  /**
   * Den Booster-Schalter setzen: `"on"` heisst stapeln, alles andere
   * verlaengern. Ein **gesetzter** Wert und kein Umschalter — sonst verlöre eine
   * verlorene Antwort den Gleichstand zwischen Knopf und Spielstand.
   */
  async boosterMode(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/boostermode/', { id });
  }

  /** Das Wunscherz der Drohnen setzen. Leere ID heisst „keins". */
  async wishOre(id: string): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/wish/', { id });
  }

  /**
   * Der Heimflug — ein bezahlter Zug ohne ID.
   *
   * Er laeuft ueber REST wie Kaufen und Bauen. Eine vierte Client-Nachricht
   * waere ein Bruch von FG-32 fuer einen Vorgang, der einmal im Leben eines
   * Spielstands stattfindet.
   */
  async homeJump(): Promise<SavePayload> {
    return this.request<SavePayload>('POST', '/api/v1/savegame/home-jump/', {});
  }

  /** Ein Kolonie-Zug. Die Uhr ist die des Servers — sie steht nicht im Körper. */
  async colony(op: ColonyOp, move: ColonyMove): Promise<SavePayload> {
    return this.request<SavePayload>('POST', `/api/v1/savegame/colony/${op}/`, move);
  }

  async wipe(): Promise<SavePayload> {
    return this.request<SavePayload>('DELETE', '/api/v1/savegame/');
  }

  /** Die WebSocket-Adresse mit Token — der einzige Ort, der ihn anhaengt. */
  socketUrl(fieldSlug?: string): string {
    const base = new URL(this.baseUrl, globalThis.location?.href ?? 'http://localhost');
    base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    base.pathname = '/ws/field/';
    base.searchParams.set('token', this.token ?? '');
    if (fieldSlug) base.searchParams.set('field', fieldSlug);
    return base.toString();
  }

  private async tryMe(): Promise<PlayerInfo | null> {
    try {
      return await this.me();
    } catch {
      return null;
    }
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.token) headers.Authorization = `Token ${this.token}`;

    const response = await fetch(this.baseUrl + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const fehler = await readError(response);
      throw new ApiFehler(fehler.code, fehler.message);
    }
    return (await response.json()) as T;
  }
}

/**
 * Den Fehler einer Antwort lesen — in **beiden** Formen, die vorkommen.
 *
 * Fachliche Fehler tragen die eigene Huelle `{"error":{code,message}}`. Alles,
 * was DRF selbst beantwortet — fehlendes Token, ungueltige Eingabe — kommt
 * dagegen als `{"detail": …}`. Ohne den zweiten Zweig wurde daraus
 * „Serverfehler 401", und „nicht angemeldet" sah aus wie „Kauf abgelehnt".
 */
async function readError(response: Response): Promise<ApiError> {
  const code = `http_${response.status}`;
  try {
    const body = (await response.json()) as { error?: ApiError; detail?: string };
    if (body.error) return body.error;
    if (typeof body.detail === 'string') return { code, message: klartext(response.status, body.detail) };
  } catch {
    // Antwort ohne JSON — der Status ist dann die ganze Information.
  }
  return { code, message: `Serverfehler ${response.status}` };
}

/**
 * Bei 401 eine handlungsleitende Meldung statt DRFs „Anmeldedaten fehlen."
 *
 * Die Uebersetzung des Frameworks ist korrekt, sagt dem Spieler aber nicht,
 * was er tun soll — und sie ist der Text, der diese Fehlersuche gekostet hat.
 */
function klartext(status: number, detail: string): string {
  return status === 401 ? 'Nicht angemeldet — bitte die Seite neu laden.' : detail;
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Ohne Speicher bekommt der Spieler beim naechsten Start ein neues Konto.
  }
}

function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ein fehlgeschlagenes Loeschen darf das Abmelden nicht verhindern.
  }
}
